<script lang="ts">
	import { createEventDispatcher } from "svelte";
	import VisibilityIcon from "@svg/VisibilityIcon.svelte";
	import VisibilityOffIcon from "@svg/VisibilityOffIcon.svelte";
	import CheckCircleIcon from "@svg/CheckCircleIcon.svelte";

	const dispatch = createEventDispatcher();

	// Base props
	export let value: string | number | null = "";
	export let type: string = "text";
	export let name: string = "";
	export let placeholder: string = "";
	export let required: boolean = false;
	export let disabled: boolean = false;
	export let readonly: boolean = false;
	export let autocomplete: HTMLInputElement['autocomplete'] = 'off';

	// Validation
	export let error: boolean = false;
	export let warning: boolean = false;
	export let success: boolean = false;
	export let minLength: number | null = null;
	export let maxLength: number | null = null;
	export let pattern: string | null = null;

	// Styling
	export let size: "sm" | "md" | "lg" = "md";
	export let borderless: boolean = false;
	let className: string = "";
	export { className as class };

	// Icons and addons
	export let leadingIcon: any = null;
	export let trailingIcon: any = null;
	export let showPasswordToggle: boolean = false;

	// State
	let passwordVisible: boolean = false;
	let inputElement: HTMLInputElement;

	// Helper functions
	function handleInput(event: Event) {
		const target = event.target as HTMLInputElement;
		value = type === "number" ? Number(target.value) : target.value;
		dispatch("input", value);
	}

	function handleFocus() {
		dispatch("focus");
	}

	function handleBlur() {
		dispatch("blur");
	}

	function togglePasswordVisibility() {
		passwordVisible = !passwordVisible;
		if (inputElement) {
			inputElement.type = passwordVisible ? "text" : "password";
		}
	}

	$: inputClasses = `
        input
        ${size === "sm" ? "py-1.5 px-3 text-sm" : size === "lg" ? "py-3 px-5 text-lg" : "py-2.5 px-4"}
        ${error ? "border-red-500 focus:border-red-500" : ""}
        ${warning ? "border-yellow-500 focus:border-yellow-500" : ""}
        ${success ? "border-green-500 focus:border-green-500" : ""}
        ${leadingIcon ? "pl-11" : ""}
        ${trailingIcon || showPasswordToggle ? "pr-11" : ""}
        ${borderless ? "" : "border"}
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        ${className}
    `;
</script>

<div class="input-wrapper">
	<div class="input-icon-container">
		{#if leadingIcon}
			<div class="input-icon-leading">
				<svelte:component this={leadingIcon} />
			</div>
		{/if}

		<input
			bind:this={inputElement}
			{type}
			{name}
			{placeholder}
			{required}
			{disabled}
			{readonly}
			{autocomplete}
			{pattern}
			minlength={minLength}
			maxlength={maxLength}
			bind:value
			class={inputClasses}
			on:input={handleInput}
			on:focus={handleFocus}
			on:blur={handleBlur}
			{...$$restProps}
		/>

		{#if showPasswordToggle && type === "password"}
			<button
				type="button"
				class="input-icon-trailing"
				on:click={togglePasswordVisibility}
			>
				{#if passwordVisible}
					<VisibilityOffIcon class="h-5 w-5" />
				{:else}
					<VisibilityIcon class="h-5 w-5" />
				{/if}
			</button>
		{:else if trailingIcon}
			<div class="input-icon-trailing">
				<svelte:component this={trailingIcon} />
			</div>
		{/if}

		{#if success}
			<div class="input-icon-trailing text-green-500">
				<CheckCircleIcon class="h-5 w-5" />
			</div>
		{/if}
	</div>

	{#if error && $$slots.error}
		<div class="input-error">
			<slot name="error" />
		</div>
	{/if}

	{#if warning && $$slots.warning}
		<div class="input-warning">
			<slot name="warning" />
		</div>
	{/if}

	{#if $$slots.helper}
		<div class="input-helper">
			<slot name="helper" />
		</div>
	{/if}
</div>
